/**
 * Kiosk Stability Utilities
 * 
 * Utilities for maintaining stability in long-running kiosk environments
 * (e.g., Fully Kiosk Browser on Android tablets).
 * 
 * Features:
 * - Smart reload with health checks (replaces dumb 2h timer)
 * - Memory pressure detection and monitoring
 * - Visibility-aware polling (pause when screen off)
 * - Network connection recovery
 * - Fetch failure tracking and auto-reload
 * - AbortSignal timeout helpers with proper cleanup
 */

const FAILURE_THRESHOLD = 5; // Reload after 5 consecutive failures
const SMART_RELOAD_CHECK_INTERVAL = 2 * 60 * 60 * 1000; // Check every 2 hours
const MEMORY_CHECK_INTERVAL = 5 * 60 * 1000; // Check memory every 5 minutes
const MEMORY_THRESHOLD = 150 * 1024 * 1024; // 150MB heap threshold
const STALE_FETCH_THRESHOLD = 10 * 60 * 1000; // 10 minutes without successful fetch
const KIOSK_MODE_KEY = 'kiosk_mode_enabled';
const HEALTH_CHECK_INTERVAL = 10 * 1000; // 10 seconds between health checks
const API_BASE = '/api/v1';

/**
 * Failure tracking state
 */
let consecutiveFailures = 0;
let lastSuccessTime = Date.now();

/**
 * API health check state
 */
let isApiDisconnected = false;
let healthCheckTimer: number | null = null;
let disconnectListeners: Array<(disconnected: boolean) => void> = [];

/**
 * Visibility and network state
 */
let isPaused = false;
let isOnline = navigator.onLine;
let activeTimers: number[] = [];
let visibilityListeners: Array<() => void> = [];

/**
 * Record a successful fetch
 */
export function recordFetchSuccess(): void {
  consecutiveFailures = 0;
  lastSuccessTime = Date.now();
}

/**
 * Record a failed fetch and trigger reload if threshold exceeded
 */
export function recordFetchFailure(): void {
  consecutiveFailures++;

  console.warn(`[Kiosk Stability] Fetch failure ${consecutiveFailures}/${FAILURE_THRESHOLD}`);

  // Start health check polling on first failure (for kiosk reconnect UI)
  if (consecutiveFailures === 1) {
    signalApiDisconnected();
  }

  if (consecutiveFailures >= FAILURE_THRESHOLD) {
    console.error(`[Kiosk Stability] Failure threshold exceeded (${FAILURE_THRESHOLD}). Reloading...`);
    triggerReload('fetch_failures');
  }
}

/**
 * Get current failure count
 */
export function getConsecutiveFailures(): number {
  return consecutiveFailures;
}

/**
 * Detect Fully Kiosk Browser (FKB) via two reliable signals:
 *   1. User-agent contains "Fully/" (e.g. "... Chrome/86 ... Fully/1.50.4")
 *   2. window.fully JS bridge object injected by FKB
 *
 * FKB is an Android WebView wrapper — NOT a PWA — so display-mode checks
 * (standalone/fullscreen) never match even when FKB launches the app
 * fullscreen. This helper catches the gap.
 */
function isFullyKioskBrowser(): boolean {
  const ua = navigator.userAgent || '';
  const hasFullyUa = / Fully\//i.test(ua) || /\bFully\b/i.test(ua);
  const hasFullyBridge =
    typeof (window as any).fully === 'object' && (window as any).fully !== null;
  return hasFullyUa || hasFullyBridge;
}

/**
 * Check if kiosk mode is enabled.
 *
 * Belt-and-braces: the URL param is no longer the only gate. The tablet
 * loads the URL without ?kiosk=1, so we also detect PWA/standalone display
 * modes and Fully Kiosk Browser directly.
 *
 * Priority order:
 *   1. URL param ?kiosk=1 / ?kiosk=true  (explicit opt-in / testing)
 *   2. localStorage kiosk_mode_enabled=true  (persisted opt-in)
 *   3. display-mode: standalone  (PWA installed to home screen)
 *   4. display-mode: fullscreen  (fullscreen PWA)
 *   5. navigator.standalone === true  (iOS Safari "Add to Home Screen")
 *   6. Fully Kiosk Browser UA / JS bridge  (Android WebView kiosk wrapper)
 */
export function isKioskModeEnabled(): boolean {
  // 1. URL param (explicit override / testing)
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.has('kiosk')) {
    return urlParams.get('kiosk') === '1' || urlParams.get('kiosk') === 'true';
  }

  // 2. localStorage persisted opt-in
  if (localStorage.getItem(KIOSK_MODE_KEY) === 'true') {
    return true;
  }

  // 3 & 4. PWA / fullscreen display mode (covers Android home-screen install
  //         and fullscreen PWA — but NOT Fully Kiosk Browser, see #6)
  if (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(display-mode: fullscreen)').matches
  ) {
    return true;
  }

  // 5. iOS Safari "Add to Home Screen"
  if ((navigator as any).standalone === true) {
    return true;
  }

  // 6. Fully Kiosk Browser (Android WebView wrapper — display-mode checks
  //    never fire here because FKB is not a PWA, PR #14 missed this)
  if (isFullyKioskBrowser()) {
    return true;
  }

  return false;
}

/**
 * Enable or disable kiosk mode
 */
export function setKioskMode(enabled: boolean): void {
  if (enabled) {
    localStorage.setItem(KIOSK_MODE_KEY, 'true');
  } else {
    localStorage.removeItem(KIOSK_MODE_KEY);
  }
}

/**
 * Check if performance.memory API is available
 */
function hasMemoryAPI(): boolean {
  return typeof performance !== 'undefined' && 
         'memory' in performance && 
         typeof (performance as any).memory === 'object';
}

/**
 * Get current heap size if available
 */
function getHeapSize(): number | null {
  if (!hasMemoryAPI()) {
    return null;
  }
  
  const memory = (performance as any).memory;
  return memory.usedJSHeapSize || null;
}

/**
 * Check memory pressure and trigger reload if threshold exceeded
 */
function checkMemoryPressure(): void {
  const heapSize = getHeapSize();
  
  if (heapSize === null) {
    // Memory API not available (not Chrome/WebView)
    return;
  }
  
  const heapMB = Math.round(heapSize / 1024 / 1024);
  
  // Log periodically for diagnostics
  console.log(`[Kiosk Stability] Heap usage: ${heapMB}MB`);
  
  if (heapSize > MEMORY_THRESHOLD) {
    const thresholdMB = Math.round(MEMORY_THRESHOLD / 1024 / 1024);
    console.warn(`[Kiosk Stability] Memory threshold exceeded (${heapMB}MB > ${thresholdMB}MB). Triggering reload...`);
    triggerReload('memory_pressure');
  }
}

/**
 * Smart reload check - only reload if unhealthy
 */
function checkSmartReload(): void {
  const timeSinceLastSuccess = Date.now() - lastSuccessTime;
  const heapSize = getHeapSize();
  
  // Check if last successful fetch is stale
  const fetchIsStale = timeSinceLastSuccess > STALE_FETCH_THRESHOLD;
  
  // Check memory if available
  const memoryOK = heapSize === null || heapSize < MEMORY_THRESHOLD;
  
  const uptimeMinutes = Math.round(timeSinceLastSuccess / 1000 / 60);
  const heapMB = heapSize ? Math.round(heapSize / 1024 / 1024) : null;
  
  if (fetchIsStale) {
    console.warn(`[Kiosk Stability] Stale fetches detected (${uptimeMinutes}m since last success). Triggering reload...`);
    triggerReload('stale_fetches');
  } else if (!memoryOK) {
    console.warn(`[Kiosk Stability] Memory pressure detected (${heapMB}MB). Triggering reload...`);
    triggerReload('memory_pressure');
  } else {
    // Everything healthy - skip reload
    console.log(
      `[Kiosk Stability] Health check passed. ` +
      `Uptime: ${uptimeMinutes}m, Heap: ${heapMB !== null ? heapMB + 'MB' : 'N/A'}. ` +
      `Skipping reload.`
    );
  }
}

/**
 * Handle visibility change (screen on/off, tab switch)
 */
function handleVisibilityChange(): void {
  if (document.hidden) {
    console.log('[Kiosk Stability] Page hidden. Pausing non-essential timers...');
    isPaused = true;
    
    // Notify listeners to pause their work
    visibilityListeners.forEach(listener => listener());
  } else {
    console.log('[Kiosk Stability] Page visible. Resuming and refreshing data...');
    isPaused = false;
    
    // Notify listeners to resume their work
    visibilityListeners.forEach(listener => listener());
    
    // Trigger a soft data refresh when coming back
    // (listeners should handle this themselves)
  }
}

/**
 * Handle online/offline events
 */
function handleOnline(): void {
  console.log('[Kiosk Stability] Connection restored. Resuming API polling...');
  isOnline = true;
  
  // Immediately trigger data refresh
  // (App-level polling should check isKioskOnline() and respond accordingly)
}

function handleOffline(): void {
  console.warn('[Kiosk Stability] Connection lost. Pausing API polling...');
  isOnline = false;
}

/**
 * Check if kiosk is online
 * Use this in app-level polling logic to pause when offline
 */
export function isKioskOnline(): boolean {
  return isOnline;
}

/**
 * Check if kiosk is paused (page hidden)
 * Use this in app-level polling logic to pause when screen is off
 */
export function isKioskPaused(): boolean {
  return isPaused;
}

/**
 * Register a listener for visibility changes
 * Callback will be invoked when page visibility changes
 * Use this to pause/resume polling in your app
 */
export function onVisibilityChange(callback: () => void): () => void {
  visibilityListeners.push(callback);
  
  // Return unsubscribe function
  return () => {
    visibilityListeners = visibilityListeners.filter(cb => cb !== callback);
  };
}

/**
 * Check if API is currently disconnected
 */
export function isApiCurrentlyDisconnected(): boolean {
  return isApiDisconnected;
}

/**
 * Register a listener for API disconnect state changes
 * Callback receives true when disconnected, false when reconnected
 */
export function onApiDisconnectChange(callback: (disconnected: boolean) => void): () => void {
  disconnectListeners.push(callback);
  return () => {
    disconnectListeners = disconnectListeners.filter(cb => cb !== callback);
  };
}

/**
 * Notify listeners of disconnect state change
 */
function notifyDisconnectListeners(disconnected: boolean): void {
  disconnectListeners.forEach(listener => listener(disconnected));
}

/**
 * Perform a health check against the API
 * Returns true if API is healthy, false otherwise
 */
async function checkApiHealth(): Promise<boolean> {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);

    const response = await fetch(`${API_BASE}/settings`, {
      method: 'GET',
      signal: controller.signal,
      credentials: 'include',
    });

    clearTimeout(timeoutId);
    return response.ok;
  } catch {
    return false;
  }
}

/**
 * Start polling the API health endpoint
 * Called when API becomes unreachable
 */
function startHealthCheckPolling(): void {
  if (healthCheckTimer !== null) {
    return; // Already polling
  }

  console.log('[Kiosk Stability] API disconnected. Starting health check polling every 10s...');
  isApiDisconnected = true;
  notifyDisconnectListeners(true);

  healthCheckTimer = window.setInterval(async () => {
    console.log('[Kiosk Stability] Checking API health...');
    const isHealthy = await checkApiHealth();

    if (isHealthy) {
      console.log('[Kiosk Stability] API is back online. Reloading page...');
      stopHealthCheckPolling();
      triggerReload('api_reconnected');
    } else {
      console.log('[Kiosk Stability] API still unreachable...');
    }
  }, HEALTH_CHECK_INTERVAL);
}

/**
 * Stop health check polling
 */
function stopHealthCheckPolling(): void {
  if (healthCheckTimer !== null) {
    clearInterval(healthCheckTimer);
    healthCheckTimer = null;
  }
}

/**
 * Signal that API has become unreachable
 * This starts the health check polling in kiosk mode
 */
export function signalApiDisconnected(): void {
  if (!isApiDisconnected) {
    startHealthCheckPolling();
  }
}

/**
 * Trigger a page reload with reason logging
 */
function triggerReload(reason: string): void {
  console.log(`[Kiosk Stability] Triggering reload. Reason: ${reason}`);
  
  // Log to analytics if available
  if (typeof window !== 'undefined' && (window as any).posthog) {
    (window as any).posthog.capture('kiosk_auto_reload', { reason });
  }
  
  // Small delay to allow logs to flush
  setTimeout(() => {
    window.location.reload();
  }, 100);
}

/**
 * Initialize kiosk stability features
 * Call this once at app startup
 *
 * In production builds the safety nets (smart reload, memory watchdog,
 * visibility/network listeners) run unconditionally — this app is a
 * tablet-kiosk display and the features should always be active in prod.
 * In dev builds they are skipped so HMR is not disrupted.
 */
export function initKioskStability(): (() => void) | null {
  if (import.meta.env.DEV) {
    console.log('[Kiosk Stability] Dev build — stability features inactive (HMR safe).');
    return null;
  }

  console.log('[Kiosk Stability] Production build — initializing stability features...');
  
  // Smart reload check (replaces dumb 2h timer)
  const smartReloadTimer = window.setInterval(() => {
    checkSmartReload();
  }, SMART_RELOAD_CHECK_INTERVAL);
  activeTimers.push(smartReloadTimer);
  
  // Memory pressure monitoring
  const memoryTimer = window.setInterval(() => {
    checkMemoryPressure();
  }, MEMORY_CHECK_INTERVAL);
  activeTimers.push(memoryTimer);
  
  // Visibility change handling
  document.addEventListener('visibilitychange', handleVisibilityChange);
  
  // Online/offline handling
  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);
  
  // Log startup info
  console.log(
    `[Kiosk Stability] Initialized. ` +
    `Smart reload checks every ${SMART_RELOAD_CHECK_INTERVAL / 1000 / 60}m, ` +
    `memory checks every ${MEMORY_CHECK_INTERVAL / 1000 / 60}m.`
  );
  
  if (hasMemoryAPI()) {
    const heapMB = Math.round((getHeapSize() || 0) / 1024 / 1024);
    console.log(`[Kiosk Stability] Memory API available. Current heap: ${heapMB}MB`);
  } else {
    console.log(`[Kiosk Stability] Memory API not available (not Chrome/WebView).`);
  }
  
  // Return cleanup function
  return () => {
    activeTimers.forEach(timer => clearInterval(timer));
    activeTimers = [];

    stopHealthCheckPolling();

    document.removeEventListener('visibilitychange', handleVisibilityChange);
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);

    visibilityListeners = [];
    disconnectListeners = [];
  };
}

/**
 * Create an AbortSignal with timeout and proper cleanup
 * Use this with fetch calls to prevent hung requests
 * 
 * @param timeoutMs - Timeout in milliseconds (default: 10000)
 * @returns Object with signal and cleanup function
 */
export function createAbortSignal(timeoutMs: number = 10000): { 
  signal: AbortSignal; 
  cleanup: () => void 
} {
  // Use native AbortSignal.timeout if available (Node 17.3+, modern browsers)
  if ('timeout' in AbortSignal && typeof (AbortSignal as any).timeout === 'function') {
    const signal = (AbortSignal as any).timeout(timeoutMs);
    return { signal, cleanup: () => {} }; // No cleanup needed for native implementation
  }
  
  // Fallback for older environments
  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    controller.abort();
  }, timeoutMs);
  
  return {
    signal: controller.signal,
    cleanup: () => clearTimeout(timeoutId)
  };
}

/**
 * Enhanced fetch wrapper with automatic timeout and failure tracking
 * 
 * @param input - Fetch input
 * @param init - Fetch init options
 * @param timeoutMs - Timeout in milliseconds (default: 10000)
 * @returns Promise with fetch response
 */
export async function fetchWithTimeout(
  input: RequestInfo | URL,
  init?: RequestInit,
  timeoutMs: number = 10000
): Promise<Response> {
  const { signal, cleanup } = createAbortSignal(timeoutMs);
  
  try {
    const response = await fetch(input, {
      ...init,
      signal,
    });
    
    cleanup();
    recordFetchSuccess();
    return response;
  } catch (error) {
    cleanup();
    
    // Only record as failure if it's a network error (not user abort)
    if (error instanceof Error && error.name !== 'AbortError') {
      recordFetchFailure();
    }
    
    throw error;
  }
}
